import React, { useState, useRef, useEffect } from "react";
import {
  ChevronLeft,
  Lightbulb,
  ChevronDown,
  User,
  Mail,
  Briefcase,
  Building,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { IoIosCloseCircle } from "react-icons/io";
import { useSelector } from "react-redux";
import {
  useApplyLeaveManuallyAsManagerMutation,
  useGetManagerReviewBundleQuery,
} from "../../slices/leaveApiSlice";
import {
  useGetUsersQuery,
  useGetDepartmentByIdQuery,
  useGetDivisionByIdQuery,
} from "../../slices/userApiSlice";

// Helper function to check if dates overlap
const hasDateOverlap = (startDate1, endDate1, startDate2, endDate2) => {
  const start1 = new Date(startDate1);
  const end1 = new Date(endDate1);
  const start2 = new Date(startDate2);
  const end2 = new Date(endDate2);

  return start1 <= end2 && end1 >= start2;
};

// Helper function to calculate days between dates
const calculateDaysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Reset time part to avoid timezone issues
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates

  return diffDays;
};

// Component to fetch and display department name
const DepartmentName = ({ departmentId }) => {
  const {
    data: department,
    isLoading,
    error,
  } = useGetDepartmentByIdQuery(departmentId, {
    skip: !departmentId,
  });

  if (isLoading) return <span className="text-gray-500">Loading...</span>;
  if (error || !department)
    return <span className="text-gray-500">Unknown Dept</span>;

  return <span>{department.name}</span>;
};

// Component to fetch and display division name
const DivisionName = ({ divisionId }) => {
  const {
    data: division,
    isLoading,
    error,
  } = useGetDivisionByIdQuery(divisionId, {
    skip: !divisionId,
  });

  if (isLoading) return <span className="text-gray-500">Loading...</span>;
  if (error || !division)
    return <span className="text-gray-500">No Division</span>;

  return <span>{division.name}</span>;
};

// Fixed helper function to get role name
const getRoleName = (user) => {
  if (!user || !user.role) return "user";
  if (typeof user.role === "object" && user.role.name) {
    return user.role.name;
  }
  if (typeof user.role === "string") {
    return user.role;
  }
  return "user";
};

// Enhanced SafeRoleDisplay component with manager highlight
const SafeRoleDisplay = ({ user }) => {
  const roleName = getRoleName(user);
  const roleColors = {
    supervisor: "bg-green-100 text-green-800",
    technician: "bg-blue-100 text-blue-800",
    manager: "bg-purple-100 text-purple-800 border border-purple-300",
    cfm: "bg-red-100 text-red-800",
    default: "bg-gray-100 text-gray-800",
  };

  const colorClass = roleColors[roleName.toLowerCase()] || roleColors.default;

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${colorClass}`}
    >
      {roleName.toLowerCase()}
      {roleName.toLowerCase() === "manager"}
    </span>
  );
};

export default function ApplyManualLeave() {
  const navigate = useNavigate();
  const suggestionsRef = useRef(null);
  const usersDropdownRef = useRef(null);

  // Get manager info from Redux store
  const { userInfo, userToken } = useSelector((state) => state.auth);

  // Enhanced manager info extraction with multiple fallbacks
  const managerId = userInfo?.userId || userInfo?.id || userInfo?._id;
  const managerEmail =
    userInfo?.email || userInfo?.userEmail || userInfo?.username;
  const managerName = userInfo?.name || userInfo?.userName || "Manager";

  // FIXED: Get department ID with comprehensive fallbacks
  const managerDepartmentId =
    userInfo?.departmentId || userInfo?.department?.id || userInfo?.department;

  // NEW: Get academy ID with comprehensive fallbacks
  const managerAcademyId =
    userInfo?.academyId || userInfo?.academy?.id || userInfo?.academy;

  // RTK Query mutation for applying manual leave
  const [applyLeaveManually, { isLoading: isSubmitting }] =
    useApplyLeaveManuallyAsManagerMutation();

  // Fetch users - with better error handling
  const {
    data: usersData = [],
    isLoading: isLoadingUsers,
    error: usersError,
  } = useGetUsersQuery();

  // Fetch existing leaves from manager review bundle
  const {
    data: reviewBundleResponse,
    isLoading: isLoadingLeaves,
    error: leavesError,
    refetch: refetchLeaves,
  } = useGetManagerReviewBundleQuery(
    { managerId, departmentId: managerDepartmentId },
    { skip: !managerId || !managerDepartmentId },
  );

  const [formData, setFormData] = useState({
    employeeId: "",
    employeeName: "",
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",
    selectedUser: null,
  });

  const [errors, setErrors] = useState({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);

  // State to track existing leaves for date validation
  const [existingLeaves, setExistingLeaves] = useState([]);

  // Common leave type suggestions
  const leaveTypeSuggestions = [
    "Medical Leave",
    "Emergency Leave",
    "Maternity Leave",
    "Paternity Leave",
    "Study Leave",
    "Sabbatical Leave",
    "Compensatory Leave",
    "Vacation Leave",
    "Sick Leave",
    "Personal Leave",
    "Bereavement Leave",
    "Marriage Leave",
    "Pilgrimage Leave",
    "Manual Leave",
  ];

  // Filter users by department and roles - ONLY SUPERVISORS AND TECHNICIANS FROM SAME DEPARTMENT
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    if (usersData && Array.isArray(usersData)) {
      let filtered = usersData;

      // Filter 1: Get users from same department ONLY
      if (managerDepartmentId) {
        filtered = filtered.filter(
          (user) => user.departmentId === managerDepartmentId,
        );
      } else {
        console.warn("⚠️ No department ID available for manager");
        setFilteredUsers([]);
        return;
      }

      // Filter 2: Include ONLY supervisors and technicians
      filtered = filtered.filter((user) => {
        const userRole = getRoleName(user).toUpperCase();
        return userRole === "SUPERVISOR" || userRole === "TECHNICIAN";
      });

      // Filter 3: Exclude current manager themselves
      filtered = filtered.filter((user) => user.userId !== managerId);

      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [usersData, managerDepartmentId, managerId]);

  // Process and transform leave data from manager review bundle
  const processLeaveData = (data) => {
    if (!data) return [];

    const allLeaves = [
      ...(data.technicianLeaves || []),
      ...(data.supervisorLeaves || []),
    ];

    return allLeaves.map((leave) => ({
      id: leave.id || leave._id,
      userId: leave.userId,
      employeeEmail:
        leave.employeeEmail ||
        leave.Email ||
        (leave.userId &&
        typeof leave.userId === "string" &&
        leave.userId.includes("@")
          ? leave.userId
          : ""),
      leaveType: leave.leaveType,
      startDate: leave.startDate,
      endDate: leave.endDate,
      status: leave.status,
      duration: leave.duration,
      reason: leave.reason,
    }));
  };

  // Load existing leaves when manager review bundle data changes
  useEffect(() => {
    if (reviewBundleResponse) {
      const processedLeaves = processLeaveData(reviewBundleResponse);
      setExistingLeaves(processedLeaves);
    } else {
      setExistingLeaves([]);
    }
  }, [reviewBundleResponse]);

  // Load existing leaves for selected user when user changes
  useEffect(() => {
    if (formData.selectedUser?.userId) {
      // Filter leaves for the selected user
      const userLeaves = existingLeaves.filter(
        (leave) =>
          leave.userId === formData.selectedUser.userId ||
          leave.employeeEmail === formData.selectedUser.email,
      );
    }
  }, [formData.selectedUser, existingLeaves]);

  // Show error overlay function
  const showError = (message, debug = "") => {
    toast.error(message, {
      description: debug,
    });
  };

  // Show success overlay function
  const showSuccess = (message = "Leave has been applied successfully!") => {
    toast.success(message);
  };

  const handleBack = () => {
    navigate("/manager/attendance");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If start date is changed and end date is before start date, clear end date
    if (name === "startDate" && formData.endDate && value > formData.endDate) {
      setFormData((prev) => ({
        ...prev,
        endDate: "",
      }));
    }

    if (name === "leaveType") {
      setShowSuggestions(true);
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData((prev) => ({
      ...prev,
      leaveType: suggestion,
    }));
    setShowSuggestions(false);
  };

  const handleLeaveTypeFocus = () => {
    setShowSuggestions(true);
  };

  const handleLeaveTypeBlur = (e) => {
    if (
      suggestionsRef.current &&
      suggestionsRef.current.contains(e.relatedTarget)
    ) {
      return;
    }
    setShowSuggestions(false);
  };

  const handleSuggestionMouseDown = (e) => {
    e.preventDefault();
  };

  // User selection handlers
  const handleUsersDropdownToggle = () => {
    setShowUsersDropdown(!showUsersDropdown);
  };

  const handleUserSelect = (user) => {
    if (!user || !user.userId) {
      showError("Invalid employee selection");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      employeeId: user.userId,
      employeeName: user.name || "Unknown User",
      selectedUser: user,
    }));

    setShowUsersDropdown(false);
    setErrors((prev) => ({ ...prev, employeeId: "" }));

    // Refetch leaves to get the latest data
    refetchLeaves();
  };

  const handleUsersDropdownBlur = (e) => {
    if (
      usersDropdownRef.current &&
      usersDropdownRef.current.contains(e.relatedTarget)
    ) {
      return;
    }
    setShowUsersDropdown(false);
  };

  // Get full name from user object
  const getUserFullName = (user) => {
    return `${user.name}`.trim() || user.email;
  };

  // Fixed calculateDays function with proper validation
  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) {
      return 0;
    }

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);

    // Reset time part to avoid timezone issues
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    // Check if end date is before start date
    if (end < start) {
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return -diffDays; // Return negative for invalid date range
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Check if there's a leave conflict for the selected employee and dates
  const checkLeaveConflict = () => {
    if (!formData.selectedUser || !formData.startDate || !formData.endDate) {
      return false;
    }

    const userLeaves = existingLeaves.filter(
      (leave) =>
        (leave.userId === formData.selectedUser.userId ||
          leave.employeeEmail === formData.selectedUser.email) &&
        // Only check approved, pending, or forwarded leaves (not rejected)
        (leave.status === "APPROVED" ||
          leave.status === "PENDING" ||
          leave.status === "FORWARDED"),
    );

    const hasConflict = userLeaves.some((leave) => {
      const datesOverlap = hasDateOverlap(
        formData.startDate,
        formData.endDate,
        leave.startDate,
        leave.endDate,
      );

      return datesOverlap;
    });
    return hasConflict;
  };

  // Get employee's existing leaves for display
  const getEmployeeLeaves = () => {
    if (!formData.selectedUser) return [];

    return existingLeaves.filter(
      (leave) =>
        (leave.userId === formData.selectedUser.userId ||
          leave.employeeEmail === formData.selectedUser.email) &&
        (leave.status === "APPROVED" ||
          leave.status === "PENDING" ||
          leave.status === "FORWARDED"),
    );
  };

  // Enhanced validation function with proper date validation
  const validateForm = () => {
    const newErrors = {};

    if (
      !formData.employeeId ||
      !formData.employeeId.trim() ||
      !formData.selectedUser
    ) {
      newErrors.employeeId = "Please select an employee";
    }

    if (!formData.leaveType || !formData.leaveType.trim()) {
      newErrors.leaveType = "Please enter or select a leave type";
    } else {
      // Check for ANY leave date repetition for the same employee
      const hasConflict = checkLeaveConflict();
      if (hasConflict) {
        newErrors.leaveType = `This employee already has a leave during the selected dates. Please select different dates.`;
      }

      // Validate that Casual and Earned leaves are not repeated unnecessarily
      if (
        formData.leaveType === "Casual Leave" ||
        formData.leaveType === "Earned Leave"
      ) {
        const today = new Date();
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);
        const totalDays = calculateDays();

        // Check if it's a very short notice (less than 2 days)
        const daysUntilStart = Math.ceil(
          (startDate - today) / (1000 * 60 * 60 * 24),
        );

        if (daysUntilStart < 2 && formData.leaveType === "Earned Leave") {
          newErrors.leaveType =
            "Earned Leave typically requires advance planning. Please use Casual Leave for short-notice requests.";
        }

        // Check for very long casual leaves (more than 5 days)
        if (totalDays > 5 && formData.leaveType === "Casual Leave") {
          newErrors.leaveType =
            "Casual Leave is typically for shorter durations. For leaves longer than 5 days, please use Earned Leave.";
        }
      }
    }

    if (!formData.startDate) {
      newErrors.startDate = "Please select a start date";
    }

    if (!formData.endDate) {
      newErrors.endDate = "Please select an end date";
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);

      // Reset time part for accurate comparison
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (end < start) {
        newErrors.endDate = "End date cannot be before start date";
      }

      // Check if leave spans more than 30 days (might need special approval)
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      if (diffDays > 30) {
        newErrors.endDate =
          "Leaves longer than 30 days require special approval. Please contact HR.";
      }
    }

    if (!formData.reason || !formData.reason.trim()) {
      newErrors.reason = "Please provide a reason for leave";
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason =
        "Please provide a more detailed reason (minimum 10 characters)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      const firstError = Object.values(errors)[0];
      if (firstError) showError(firstError);
      return;
    }

    // FIXED: Better department check with error handling
    if (!managerDepartmentId) {
      showError(
        "Cannot determine your department. Please ensure your manager account has a department assigned.",
        "Manager department ID is undefined. Check user profile setup.",
      );
      return;
    }

    // Check for leave conflicts before submitting
    const hasConflict = checkLeaveConflict();
    if (hasConflict) {
      showError(
        `Cannot apply leave for the selected dates because ${getUserFullName(formData.selectedUser)} already has a leave during this period.`,
        `Employee: ${getUserFullName(formData.selectedUser)}, Dates: ${formData.startDate} to ${formData.endDate}`,
      );
      return;
    }

    // Check department match
    const isEmployeeInManagerDepartment =
      formData.selectedUser?.departmentId === managerDepartmentId;

    try {
      const leaveTypeMap = {
        "Medical Leave": "MEDICAL",
        "Casual Leave": "CASUAL",
        "Emergency Leave": "EMERGENCY",
        "Maternity Leave": "MATERNITY",
        "Paternity Leave": "PATERNITY",
        "Study Leave": "STUDY",
        "Sabbatical Leave": "SABBATICAL",
        "Compensatory Leave": "COMPENSATORY",
        "Vacation Leave": "VACATION",
        "Sick Leave": "SICK",
        "Personal Leave": "PERSONAL",
        "Bereavement Leave": "BEREAVEMENT",
        "Marriage Leave": "MARRIAGE",
        "Pilgrimage Leave": "PILGRIMAGE",
        "Earned Leave": "EARNED",
        "Manual Leave": "MANUAL",
      };

      const backendLeaveType = leaveTypeMap[formData.leaveType] || "CASUAL";

      // Calculate duration
      let duration = "FULL_DAY";
      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        if (diffDays > 1) {
          duration = "MULTI_DAY";
        }
      }

      // Calculate total days for database
      const totalDays = calculateDays();

      // ✅ CORRECTED PAYLOAD - Include totalDays and force employee email to be used
      const leaveRequest = {
        // EMPLOYEE INFORMATION - The person taking the leave
        userId: formData.selectedUser.userId,
        employeeEmail: formData.selectedUser.email, // ✅ Selected employee's email
        email: formData.selectedUser.email, // ✅ Also send as 'email' field
        userEmail: formData.selectedUser.email, // ✅ And as 'userEmail' field

        // Leave details
        leaveType: backendLeaveType,
        duration: duration,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        totalDays: totalDays, // ✅ Added totalDays to payload
        reason: formData.reason.trim(),

        // Manager information - For approval tracking only
        managerId: managerId,
        managerEmail: managerEmail,
        managerName: managerName,

        // Employee role and department
        applicantRole: getRoleName(formData.selectedUser).toUpperCase(),
        departmentId: formData.selectedUser.departmentId,
        divisionId: formData.selectedUser.divisionId || null,
        academyId:
          formData.selectedUser.academyId ||
          managerAcademyId ||
          "68d1bab5751d077042a27a1c",

        // Application metadata
        applicationType: "MANUAL",
        submittedBy: managerEmail, // Manager is submitting, but leave is FOR employee
      };

      // Add supervisor fields if needed
      const selectedUserRole = getRoleName(formData.selectedUser).toUpperCase();
      if (
        (selectedUserRole === "TECHNICIAN" ||
          selectedUserRole === "SUPERVISOR") &&
        formData.selectedUser.supervisorId
      ) {
        leaveRequest.supervisorId = formData.selectedUser.supervisorId;
        leaveRequest.supervisorEmail = formData.selectedUser.supervisorEmail;
      }

      // Show success
      // Call API
      const promise = applyLeaveManually(leaveRequest).unwrap();

      toast.promise(promise, {
        loading: "Submitting leave application...",
        success: (data) => {
          refetchLeaves();
          // Reset form and navigate after success
          setFormData({
            employeeId: "",
            employeeName: "",
            leaveType: "",
            startDate: "",
            endDate: "",
            reason: "",
            selectedUser: null,
          });
          setTimeout(
            () => navigate("/manager/attendance/employee-leave-requests"),
            1000,
          );
          return `Leave application for ${getUserFullName(formData.selectedUser)} has been submitted successfully!`;
        },
        error: (err) => {
          return "Failed to submit leave application. Please try again.";
        },
      });
    } catch (error) {
      showError("An unexpected error occurred. Please try again.");
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const totalDays = calculateDays();

  // Filter suggestions based on current input
  const filteredSuggestions = leaveTypeSuggestions.filter((suggestion) =>
    suggestion.toLowerCase().includes(formData.leaveType.toLowerCase()),
  );

  // Check if current dates have leave conflict for this employee
  const hasLeaveConflict = checkLeaveConflict();
  const employeeLeaves = getEmployeeLeaves();

  return (
    <div className="p-8 max-w-10xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-1 mb-6">
        <button
          onClick={handleBack}
          className="w-9 h-9 flex items-center justify-center  rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
        >
          <ChevronLeft size={24} strokeWidth={2} className="text-gray-700" />
        </button>
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-bold text-gray-800">
            Apply Manual Leave
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2">
          <div className="bg-[#FAFAFA] rounded-lg p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Employee Selection Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Employee *
                </label>
                <div className="relative" ref={usersDropdownRef}>
                  <button
                    type="button"
                    onClick={handleUsersDropdownToggle}
                    onBlur={handleUsersDropdownBlur}
                    className={`w-full border-2 rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-gray-400 transition-colors ${
                      errors.employeeId ? "border-red-500" : "border-gray-300"
                    } ${formData.selectedUser ? "text-gray-900" : "text-gray-500"}`}
                  >
                    <div className="flex items-center space-x-3 truncate">
                      {formData.selectedUser ? (
                        <>
                          <User className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          <div className="truncate">
                            <div className="font-medium text-sm">
                              {getUserFullName(formData.selectedUser)}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {formData.selectedUser.email}
                            </div>
                          </div>
                        </>
                      ) : (
                        <span>
                          {managerDepartmentId
                            ? "Select supervisors & technicians from your department"
                            : "Department not configured - cannot load employees"}
                        </span>
                      )}
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform ${showUsersDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {showUsersDropdown && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                      {isLoadingUsers ? (
                        <div className="px-4 py-3 text-center text-gray-500">
                          Loading employees...
                        </div>
                      ) : !managerDepartmentId ? (
                        <div className="px-4 py-3 text-center text-yellow-600">
                          Your manager account doesn't have a department
                          assigned. Please contact admin.
                        </div>
                      ) : filteredUsers.length === 0 ? (
                        <div className="px-4 py-3 text-center text-gray-500">
                          No supervisors or technicians found in your department
                        </div>
                      ) : (
                        filteredUsers.map((user, index) => (
                          <button
                            key={
                              user.userId || user.employeeId || `user-${index}`
                            }
                            type="button"
                            onClick={() => handleUserSelect(user)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-blue-600" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-semibold text-gray-900 text-sm truncate">
                                    {getUserFullName(user)}
                                  </h4>
                                  <SafeRoleDisplay user={user} />
                                </div>
                                <div className="mt-1 space-y-1">
                                  <div className="flex items-center text-xs text-gray-600">
                                    <Mail className="w-3 h-3 mr-1" />
                                    <span className="truncate">
                                      {user.email}
                                    </span>
                                  </div>
                                  <div className="flex items-center text-xs text-gray-600">
                                    <Briefcase className="w-3 h-3 mr-1" />
                                    <span className="truncate">
                                      <DepartmentName
                                        departmentId={user.departmentId}
                                      />
                                    </span>
                                  </div>
                                  {user.divisionId && (
                                    <div className="flex items-center text-xs text-gray-600">
                                      <Building className="w-3 h-3 mr-1" />
                                      <span className="truncate">
                                        <DivisionName
                                          divisionId={user.divisionId}
                                        />
                                      </span>
                                    </div>
                                  )}
                                  {user.employeeId && (
                                    <div className="flex items-center text-xs text-gray-500">
                                      <Users className="w-3 h-3 mr-1" />
                                      <span>ID: {user.employeeId}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {errors.employeeId && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.employeeId}
                  </p>
                )}
                {managerDepartmentId ? (
                  <p className="mt-1 text-xs text-gray-500">
                    Showing supervisors & technicians from your department only
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-yellow-600">
                    ⚠️ Your manager account doesn't have a department assigned.
                    Please contact admin.
                  </p>
                )}
              </div>

              {/* Leave Type - Text Input with Suggestions */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Leave Type *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="leaveType"
                    placeholder="Type or select a leave type"
                    value={formData.leaveType}
                    onChange={handleInputChange}
                    onFocus={handleLeaveTypeFocus}
                    onBlur={handleLeaveTypeBlur}
                    className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${
                      errors.leaveType ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>

                {/* Suggestions Dropdown */}
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div
                    ref={suggestionsRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  >
                    {filteredSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSuggestionClick(suggestion)}
                        onMouseDown={handleSuggestionMouseDown}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}

                {errors.leaveType && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.leaveType}
                  </p>
                )}
              </div>

              {/* Date Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    min={today}
                    className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.startDate
                        ? "border-red-500 bg-red-50"
                        : "border-gray-300"
                    } ${hasLeaveConflict ? "border-red-500 bg-gray-50" : ""}`}
                  />
                  {errors.startDate && (
                    <p className="mt-1 text-sm text-red-600 font-medium">
                      {errors.startDate}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {hasLeaveConflict && (
                      <span className="text-red-600 block mt-1">
                        You already have leave scheduled for these dates
                      </span>
                    )}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate || today} // This will disable dates before start date
                    className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.endDate
                        ? "border-red-500 bg-red-50"
                        : "border-gray-300"
                    } ${hasLeaveConflict ? "border-red-500 bg-gray-50" : ""}`}
                  />
                  {errors.endDate && (
                    <p className="mt-1 text-sm text-red-600 font-medium">
                      {errors.endDate}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {hasLeaveConflict && (
                      <span className="text-red-600 block mt-1">
                        You already have leave scheduled for these dates
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Total Days Display */}
              {formData.startDate && formData.endDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Days
                  </label>
                  <div
                    className={`w-full border-2 rounded-lg px-4 py-3 ${
                      totalDays < 0
                        ? "bg-red-50 border-red-500 text-red-700"
                        : hasLeaveConflict
                          ? ""
                          : "bg-gray-50 border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>
                        {totalDays < 0
                          ? `Invalid date range (${Math.abs(totalDays)} days)`
                          : `${totalDays} ${totalDays === 1 ? "day" : "days"}`}
                      </span>
                      {totalDays < 0 && (
                        <span className="text-xs text-red-600 font-medium">
                          (End date cannot be before start date)
                        </span>
                      )}
                    </div>
                  </div>
                  {totalDays < 0 && (
                    <p className="mt-1 text-sm text-red-600">
                      Please select an end date that is after the start date
                    </p>
                  )}
                </div>
              )}

              {/* Reason for Leave */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Leave *
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Please provide a detailed reason for leave request (minimum 10 characters)"
                  className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.reason ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.reason && (
                  <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.selectedUser ||
                    !managerDepartmentId ||
                    hasLeaveConflict ||
                    totalDays < 0
                  }
                  className="w-full bg-black text-white py-3 px-6 rounded-lg font-semibold hover:bg-zinc-800 transition-colors mb-4 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Applying Leave...</span>
                    </>
                  ) : !managerDepartmentId ? (
                    <span>Department Not Configured</span>
                  ) : hasLeaveConflict ? (
                    <span>Leave Conflict - Change Dates</span>
                  ) : totalDays < 0 ? (
                    <span>Invalid Date Range</span>
                  ) : (
                    <span>Apply Leave</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column - How it works */}
        <div className="space-y-6">
          {/* Selected Employee Preview */}
          {formData.selectedUser && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3">
                Selected Employee
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-green-800">
                    Name:
                  </span>
                  <span className="text-sm text-green-700">
                    {getUserFullName(formData.selectedUser)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-green-800">
                    Email:
                  </span>
                  <span className="text-sm text-green-700">
                    {formData.selectedUser.email}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-green-800">
                    Role:
                  </span>
                  <span className="text-sm text-green-700 capitalize">
                    {getRoleName(formData.selectedUser).toLowerCase()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-green-800">
                    Department:
                  </span>
                  <span className="text-sm text-green-700">
                    <DepartmentName
                      departmentId={formData.selectedUser.departmentId}
                    />
                  </span>
                </div>
                {formData.selectedUser.divisionId && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-green-800">
                      Division:
                    </span>
                    <span className="text-sm text-green-700">
                      <DivisionName
                        divisionId={formData.selectedUser.divisionId}
                      />
                    </span>
                  </div>
                )}
                {formData.selectedUser.employeeId && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-green-800">
                      Employee ID:
                    </span>
                    <span className="text-sm text-green-700">
                      {formData.selectedUser.employeeId}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* How it works section */}
          <div className="bg-[#FAFAFA] border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-2 mb-2">
              <Lightbulb className="w-5 h-5 text-black-600 mt-0.1 ml-1 flex-shrink-0" />
              <h3 className="font-semibold text-gray-800">How it works</h3>
            </div>
            <ul className="text-sm text-gray-700 space-y-3 ml-2">
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">1.</span>
                <span>
                  Select supervisors or technician from your department
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">2.</span>
                <span>Choose appropriate leave type</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">3.</span>
                <span>Provide detailed reason for leave</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">4.</span>
                <span>Leave is automatically applied for the employee</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
